export const CONVERSATION_LIST_KEYS = [
  'me',
  'unassigned',
  'all',
  'appliedFilters',
];

export const getListArray = (state, listKey) => {
  const lists = state.conversationLists;
  if (!lists || !lists[listKey]) return [];
  return lists[listKey];
};

export const findConversationByDisplayId = (state, id) => {
  if (id == null || id === '') return undefined;
  const n = Number(id);
  for (let i = 0; i < CONVERSATION_LIST_KEYS.length; i += 1) {
    const key = CONVERSATION_LIST_KEYS[i];
    const list = getListArray(state, key);
    const found = list.find(c => Number(c.id) === n);
    if (found) return found;
  }
  return undefined;
};

export const getSelectedChatConversation = state => {
  const { selectedChatId } = state;
  if (selectedChatId == null || selectedChatId === '') return [];
  const found = findConversationByDisplayId(state, selectedChatId);
  return found ? [found] : [];
};

export const activityScoreForSort = conversation => {
  const { last_activity_at: lastActivityAt, timestamp, created_at: createdAt } =
    conversation || {};
  if (lastActivityAt != null && lastActivityAt !== '') {
    return Number(lastActivityAt);
  }
  if (timestamp != null && timestamp !== '') {
    return Number(timestamp);
  }
  if (createdAt != null && createdAt !== '') {
    return Number(createdAt);
  }
  return 0;
};

/**
 * If the conversation is already in the list, keep its index (stable order on open/update).
 * If it is new to this list, insert by last_activity_at descending (same as "Latest" sort).
 */
export const upsertConversationInListSortedByLatest = (list, conversation) => {
  if (!conversation || conversation.id == null || conversation.id === '') {
    return;
  }
  const n = Number(conversation.id);
  const existingIdx = list.findIndex(c => Number(c.id) === n);
  if (existingIdx !== -1) {
    return;
  }
  const score = activityScoreForSort(conversation);
  let insertAt = list.findIndex(c => activityScoreForSort(c) < score);
  if (insertAt === -1) {
    insertAt = list.length;
  }
  list.splice(insertAt, 0, conversation);
};

export const ensureConversationInList = (list, conversation) => {
  const n = Number(conversation.id);
  if (!list.some(c => Number(c.id) === n)) {
    list.push(conversation);
  }
};

export const removeConversationFromList = (list, conversationId) => {
  const n = Number(conversationId);
  const idx = list.findIndex(c => Number(c.id) === n);
  if (idx !== -1) list.splice(idx, 1);
};

export const mergeConversationAttributes = (existing, incoming) => {
  const { messages, ...conversationAttributes } = incoming;
  const mergedMeta = conversationAttributes.meta
    ? { ...existing.meta, ...conversationAttributes.meta }
    : existing.meta;
  return {
    ...existing,
    ...conversationAttributes,
    meta: mergedMeta,
  };
};

export const mergeMessageRecords = (stored, incoming) => {
  if (!stored) return incoming;
  if (!incoming) return stored;

  const merged = { ...stored, ...incoming };
  const storedAttachments = stored.attachments || [];
  const incomingAttachments = incoming.attachments || [];

  if (incomingAttachments.length > 0) {
    merged.attachments = incomingAttachments;
  } else if (storedAttachments.length > 0) {
    merged.attachments = storedAttachments;
  }

  return merged;
};

/** Merge message arrays by id (order of arrays = later arrays win on scalar fields). */
export const mergeMessagesById = (...messageLists) => {
  const byId = new Map();

  messageLists.forEach(list => {
    (list || []).forEach(message => {
      if (!message || message.id == null) return;
      const id = Number(message.id);
      byId.set(id, mergeMessageRecords(byId.get(id), message));
    });
  });

  return [...byId.values()];
};

export const reconcileAssigneeListMembership = (
  state,
  conversation,
  currentUserId
) => {
  if (!conversation) return;
  const lists = state.conversationLists;
  const assigneeId = conversation.meta?.assignee?.id;

  upsertConversationInListSortedByLatest(lists.all, conversation);

  if (currentUserId === undefined || currentUserId === null) {
    if (!assigneeId) {
      upsertConversationInListSortedByLatest(lists.unassigned, conversation);
    }
    return;
  }

  const shouldMe = Number(assigneeId) === Number(currentUserId);
  const shouldUnassigned = !assigneeId;

  const inMe = lists.me.some(c => Number(c.id) === Number(conversation.id));
  const inUnassigned = lists.unassigned.some(
    c => Number(c.id) === Number(conversation.id)
  );

  if (inMe && !shouldMe) {
    removeConversationFromList(lists.me, conversation.id);
  } else if (!inMe && shouldMe) {
    upsertConversationInListSortedByLatest(lists.me, conversation);
  }

  if (inUnassigned && !shouldUnassigned) {
    removeConversationFromList(lists.unassigned, conversation.id);
  } else if (!inUnassigned && shouldUnassigned) {
    upsertConversationInListSortedByLatest(lists.unassigned, conversation);
  }
};

export const listKeysForNewConversation = (conversation, currentUserId) => {
  const assigneeId = conversation.meta?.assignee?.id;
  const keys = ['all'];
  if (!assigneeId) {
    keys.push('unassigned');
  } else if (
    currentUserId !== undefined &&
    currentUserId !== null &&
    Number(assigneeId) === Number(currentUserId)
  ) {
    keys.push('me');
  }
  return keys;
};
